// The setup wizard's first fleet sync, as the firm-sync socket events report
// it. The API reports a failed availability or bookings phase and then
// completes the run anyway, so a completed run is not a successful one: the
// wizard moves on by itself only when nothing failed.

export type SetupPhase = 'yachts' | 'availability' | 'reservations';

/** The order the charter sync runs its phases in; a retry runs from one onward. */
export const SETUP_PHASES: readonly SetupPhase[] = ['yachts', 'availability', 'reservations'];

export interface PhaseState {
  status: 'pending' | 'running' | 'completed' | 'failed';
  completed: number;
  total: number;
  stats: Record<string, number> | null;
  error: string | null;
}

export interface SetupSyncState extends Record<SetupPhase, PhaseState> {
  /** The run ended: the API sent its completion event. */
  finished: boolean;
  /** Whether the API says the last failure can be retried. */
  retryable: boolean;
}

export type SetupSyncAction =
  | { type: 'PHASE_STARTED'; phase: SetupPhase; total: number }
  | { type: 'PHASE_PROGRESS'; phase: SetupPhase; completed: number; total: number }
  | { type: 'PHASE_COMPLETED'; phase: SetupPhase; stats: Record<string, number> }
  | { type: 'SYNC_FAILED'; phase: SetupPhase; error: string; retryable: boolean }
  | { type: 'SYNC_COMPLETED' }
  | { type: 'RETRY_STARTED'; phase: SetupPhase };

const pending: PhaseState = { status: 'pending', completed: 0, total: 0, stats: null, error: null };

export const initialSetupSyncState: SetupSyncState = {
  yachts: pending,
  availability: pending,
  reservations: pending,
  finished: false,
  retryable: false,
};

export function setupSyncReducer(state: SetupSyncState, action: SetupSyncAction): SetupSyncState {
  switch (action.type) {
    case 'PHASE_STARTED':
      return {
        ...state,
        [action.phase]: { ...pending, status: 'running', total: action.total },
      };
    case 'PHASE_PROGRESS':
      return {
        ...state,
        [action.phase]: {
          ...state[action.phase],
          completed: action.completed,
          total: action.total,
        },
      };
    case 'PHASE_COMPLETED':
      return {
        ...state,
        [action.phase]: { ...state[action.phase], status: 'completed', stats: action.stats },
      };
    case 'SYNC_FAILED':
      return {
        ...state,
        retryable: action.retryable,
        [action.phase]: { ...state[action.phase], status: 'failed', error: action.error },
      };
    case 'SYNC_COMPLETED':
      return { ...state, finished: true };
    case 'RETRY_STARTED': {
      // The phases before the one retried are skipped by the API and keep their result.
      const from = SETUP_PHASES.indexOf(action.phase);
      const reset = Object.fromEntries(SETUP_PHASES.slice(from).map((p) => [p, pending]));
      return { ...state, ...reset, finished: false, retryable: false };
    }
  }
}

/** The failed phases, in the order they run. */
export const failedPhases = (state: SetupSyncState): SetupPhase[] =>
  SETUP_PHASES.filter((p) => state[p].status === 'failed');

/** Where a retry starts: the earliest failed phase, so none of them is skipped. */
export const retryFrom = (state: SetupSyncState): SetupPhase | null =>
  failedPhases(state)[0] ?? null;

/**
 * Whether the run is over. A failed yachts phase ends it without a completion
 * event; any other failure is followed by the remaining phases, and a retry
 * started meanwhile would clash with the run still going.
 */
const ended = (state: SetupSyncState) => state.finished || state.yachts.status === 'failed';

export const autoAdvances = (state: SetupSyncState): boolean =>
  state.finished && failedPhases(state).length === 0;

export const canRetry = (state: SetupSyncState): boolean =>
  ended(state) && state.retryable && retryFrom(state) !== null;

export const canContinue = (state: SetupSyncState): boolean =>
  state.finished && failedPhases(state).length > 0;

/** What a completed phase synced: its upserts or rows seen when it reports them, else its total. */
export const syncedCount = (phase: PhaseState): number =>
  phase.stats?.['upserted'] ?? phase.stats?.['seen'] ?? phase.total;
